/**
 * Analytics Interface for Booking Widget
 * 
 * Higher-level analytics utilities that:
 * - Auto-include property context from the widget
 * - Provide simple, named functions for common events
 * - Handle edge cases gracefully
 * 
 * Usage:
 *   import { trackWidgetLoad, trackBookingCompleted } from '@/lib/analytics';
 *   
 *   // In component
 *   trackWidgetLoad(propertyId, propertyName);
 *   trackBookingCompleted({ propertyId, experienceId, value: 99.99, currency: 'USD' });
 */

import { trackWidgetEvent, widgetEvents, initPlausible } from './plausible';

// Re-export init function
export { initPlausible };

// Current property context (set when widget loads)
let currentPropertyContext: {
  propertyId: string;
  propertyName?: string;
} | null = null;

/**
 * Set the property context for all subsequent events
 * Call this when the widget first loads with property data
 */
export function setPropertyContext(propertyId: string, propertyName?: string): void {
  currentPropertyContext = { propertyId, propertyName };
}

/**
 * Get the current property context
 */
export function getPropertyContext() {
  return currentPropertyContext;
}

/**
 * Clear the property context (e.g., on widget unmount)
 */
export function clearPropertyContext(): void {
  currentPropertyContext = null;
}

// ============================================
// Event Tracking Functions
// ============================================

/**
 * Track when the widget loads
 */
export function trackWidgetLoad(propertyId: string, propertyName?: string): void {
  // Also set context for future events
  setPropertyContext(propertyId, propertyName);

  trackWidgetEvent(widgetEvents.widgetLoad, {
    propertyId,
    propertyName,
    referrer: document.referrer || undefined,
  });
}

/**
 * Track when a user views an experience
 */
export function trackExperienceViewed(
  experienceId: string,
  experienceName?: string
): void {
  if (!currentPropertyContext) {
    console.warn('[Analytics] No property context set for experienceViewed');
    return;
  }

  trackWidgetEvent(widgetEvents.experienceViewed, {
    propertyId: currentPropertyContext.propertyId,
    experienceId,
    experienceName,
  });
}

/**
 * Track when a user selects a date
 */
export function trackDateSelected(experienceId: string, date?: string): void {
  if (!currentPropertyContext) {
    console.warn('[Analytics] No property context set for dateSelected');
    return;
  }

  trackWidgetEvent(widgetEvents.dateSelected, {
    propertyId: currentPropertyContext.propertyId,
    experienceId,
    date,
  });
}

/**
 * Track when user starts the booking flow (clicks "Book Now" or similar)
 */
export function trackBookingStarted(
  experienceId: string,
  experienceName?: string
): void {
  if (!currentPropertyContext) {
    console.warn('[Analytics] No property context set for bookingStarted');
    return;
  }

  trackWidgetEvent(widgetEvents.bookingStarted, {
    propertyId: currentPropertyContext.propertyId,
    experienceId,
    experienceName,
  });
}

/**
 * Track when user enters checkout (guest details form)
 */
export function trackCheckoutStarted(
  experienceId: string,
  guestCount?: number
): void {
  if (!currentPropertyContext) {
    console.warn('[Analytics] No property context set for checkoutStarted');
    return;
  }

  trackWidgetEvent(widgetEvents.checkoutStarted, {
    propertyId: currentPropertyContext.propertyId,
    experienceId,
    guestCount,
  });
}

/**
 * Track when payment processing begins
 */
export function trackPaymentStarted(
  experienceId: string,
  value?: number,
  currency?: string
): void {
  if (!currentPropertyContext) {
    console.warn('[Analytics] No property context set for paymentStarted');
    return;
  }

  trackWidgetEvent(widgetEvents.paymentStarted, {
    propertyId: currentPropertyContext.propertyId,
    experienceId,
    value,
    currency,
  });
}

/**
 * Track when a booking is successfully completed
 */
export function trackBookingCompleted(params: {
  experienceId: string;
  experienceName?: string;
  value?: number;
  currency?: string;
  bookingNumber?: string;
}): void {
  if (!currentPropertyContext) {
    console.warn('[Analytics] No property context set for bookingCompleted');
    return;
  }

  trackWidgetEvent(widgetEvents.bookingCompleted, {
    propertyId: currentPropertyContext.propertyId,
    ...params,
  });
}

/**
 * Track when payment fails
 */
export function trackPaymentFailed(experienceId: string, error?: string): void {
  if (!currentPropertyContext) {
    console.warn('[Analytics] No property context set for paymentFailed');
    return;
  }

  trackWidgetEvent(widgetEvents.paymentFailed, {
    propertyId: currentPropertyContext.propertyId,
    experienceId,
    error,
  });
}

/**
 * Track when a user abandons the booking flow
 */
export function trackBookingAbandoned(
  step: 'experience' | 'date' | 'checkout' | 'payment',
  experienceId?: string
): void {
  if (!currentPropertyContext) {
    // Still try to track with unknown property
    trackWidgetEvent(widgetEvents.bookingAbandoned, {
      propertyId: 'unknown',
      step,
      experienceId,
    });
    return;
  }

  trackWidgetEvent(widgetEvents.bookingAbandoned, {
    propertyId: currentPropertyContext.propertyId,
    step,
    experienceId,
  });
}

/**
 * Track widget errors
 */
export function trackWidgetError(
  error: string,
  component?: string,
  errorType?: string
): void {
  if (!currentPropertyContext) {
    trackWidgetEvent(widgetEvents.widgetError, {
      propertyId: 'unknown',
      error,
      component,
      errorType,
    });
    return;
  }

  trackWidgetEvent(widgetEvents.widgetError, {
    propertyId: currentPropertyContext.propertyId,
    error,
    component,
    errorType,
  });
}

/**
 * Track widget load failures
 */
export function trackWidgetLoadFailed(error: string, stage?: string): void {
  if (!currentPropertyContext) {
    trackWidgetEvent(widgetEvents.widgetLoadFailed, {
      propertyId: 'unknown',
      error,
      stage,
    });
    return;
  }

  trackWidgetEvent(widgetEvents.widgetLoadFailed, {
    propertyId: currentPropertyContext.propertyId,
    error,
    stage,
  });
}

/**
 * Track experience search
 */
export function trackExperienceSearch(query: string, resultCount: number): void {
  if (!currentPropertyContext) {
    console.warn('[Analytics] No property context set for experienceSearch');
    return;
  }

  trackWidgetEvent(widgetEvents.experienceSearch, {
    propertyId: currentPropertyContext.propertyId,
    query,
    resultCount,
  });
}

/**
 * Track experience list viewed (category/filter views)
 */
export function trackExperienceListViewed(
  experienceCount: number,
  category?: string,
  filter?: string
): void {
  if (!currentPropertyContext) {
    console.warn('[Analytics] No property context set for experienceListViewed');
    return;
  }

  trackWidgetEvent(widgetEvents.experienceListViewed, {
    propertyId: currentPropertyContext.propertyId,
    category,
    filter,
    experienceCount,
  });
}

/**
 * Track promo code entered
 */
export function trackPromoCodeEntered(
  experienceId: string,
  code: string,
  valid: boolean,
  discountAmount?: number
): void {
  if (!currentPropertyContext) {
    console.warn('[Analytics] No property context set for promoCodeEntered');
    return;
  }

  trackWidgetEvent(widgetEvents.promoCodeEntered, {
    propertyId: currentPropertyContext.propertyId,
    experienceId,
    code,
    valid,
    discountAmount,
  });
}

/**
 * Track price displayed (for price comparison analytics)
 */
export function trackPriceDisplayed(
  experienceId: string,
  basePrice: number,
  finalPrice: number,
  currency: string,
  hasDiscount: boolean
): void {
  if (!currentPropertyContext) {
    console.warn('[Analytics] No property context set for priceDisplayed');
    return;
  }

  trackWidgetEvent(widgetEvents.priceDisplayed, {
    propertyId: currentPropertyContext.propertyId,
    experienceId,
    basePrice,
    finalPrice,
    currency,
    hasDiscount,
  });
}

// ============================================
// Abandonment Detection
// ============================================

let abandonmentHandler: (() => void) | null = null;
let currentStep: 'experience' | 'date' | 'checkout' | 'payment' | null = null;
let currentExperienceId: string | null = null;
let bookingCompleted = false;

/**
 * Start tracking abandonment for the booking flow
 * Call this when user enters the booking flow
 */
export function startAbandonmentTracking(
  step: 'experience' | 'date' | 'checkout' | 'payment',
  experienceId?: string
): void {
  currentStep = step;
  currentExperienceId = experienceId ?? null;
  bookingCompleted = false;

  // Only set up handler once
  if (!abandonmentHandler) {
    abandonmentHandler = () => {
      if (!bookingCompleted && currentStep) {
        trackBookingAbandoned(currentStep, currentExperienceId ?? undefined);
      }
    };

    window.addEventListener('beforeunload', abandonmentHandler);
    window.addEventListener('pagehide', abandonmentHandler);
  }
}

/**
 * Update the current step in the booking flow
 */
export function updateAbandonmentStep(
  step: 'experience' | 'date' | 'checkout' | 'payment',
  experienceId?: string
): void {
  currentStep = step;
  if (experienceId) {
    currentExperienceId = experienceId;
  }
}

/**
 * Mark booking as completed (prevents abandonment tracking)
 */
export function markBookingCompleted(): void {
  bookingCompleted = true;
}

/**
 * Stop abandonment tracking (e.g., when widget unmounts)
 */
export function stopAbandonmentTracking(): void {
  if (abandonmentHandler) {
    window.removeEventListener('beforeunload', abandonmentHandler);
    window.removeEventListener('pagehide', abandonmentHandler);
    abandonmentHandler = null;
  }
  currentStep = null;
  currentExperienceId = null;
  bookingCompleted = false;
}

// ============================================
// Template & Instance Tracking
// ============================================

/**
 * Track template render event
 */
export function trackTemplateRender(
  template: string,
  instanceKey: string,
  experienceCount: number
): void {
  if (!currentPropertyContext) {
    console.warn('[Analytics] No property context set for templateRender');
    return;
  }

  trackWidgetEvent('Template Render', {
    propertyId: currentPropertyContext.propertyId,
    template,
    instanceKey,
    experienceCount,
  });
}

/**
 * Track template interaction (carousel next, filter apply, etc.)
 */
export function trackTemplateInteraction(
  template: string,
  action: string,
  metadata?: Record<string, string | number>
): void {
  if (!currentPropertyContext) {
    return;
  }

  trackWidgetEvent('Template Interaction', {
    propertyId: currentPropertyContext.propertyId,
    template,
    action,
    ...metadata,
  });
}

/**
 * Track instance load (for multi-instance analytics)
 */
export function trackInstanceLoad(
  instanceKey: string,
  template: string,
  contentType: string
): void {
  if (!currentPropertyContext) {
    return;
  }

  trackWidgetEvent('Instance Load', {
    propertyId: currentPropertyContext.propertyId,
    instanceKey,
    template,
    contentType,
  });
}
